// Composables
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/store/modules/auth";
import { useSettingsStore } from "@/store/modules/settings";
import { requestMicPermission } from "@/composables/useMicPermission";
const routes = [
  {
    path: "/",
    redirect: "/select-language",
  },
  {
    path: "/select-language",
    component: () => import("@/views/SelectLanguage.vue"),
    name: "selectlang",
    meta: { requiresAuth: false },
  },
  {
    path: "/login",
    component: () => import("@/views/Login.vue"),
    name: "login",
    meta: { requiresAuth: false },
  },
  {
    path: "/select-riwaya",
    component: () => import("@/views/SelectRiwaya.vue"),
    name: "selectriwaya",
    meta: { requiresAuth: true },
  },
  {
    path: "/forgot-password",
    component: () => import("@/views/ForgotPassword.vue"),
    name: "forgotPassword",
    meta: { requiresAuth: false },
  },
  {
    path: "/reset-password",
    component: () => import("@/views/ResetPassword.vue"),
    name: "resetPassword",
    meta: { requiresAuth: false },
  },
  {
    path: "/terms",
    component: () => import("@/views/Terms.vue"),
    name: "terms",
    meta: { requiresAuth: false },
  },
  {
    path: "/app",
    component: () => import("@/layouts/Layout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "/pron",
        component: () => import("@/views/pron/Pronounce.vue"),
        name: "pronounce",
        meta: { requiresAuth: true },
        beforeEnter: async (to, from, next) => {
          // Request microphone permission when entering /pron
          await requestMicPermission();
          next();
        },
      },
      {
        path: "/activities",
        component: () => import("@/views/ActivityHub.vue"),
        name: "activities",
        meta: { requiresAuth: false },
      },
      {
        path: "/mushaf/:activity",
        component: () => import("@/views/mushaf/MushafView.vue"),
        name: "mushaf",
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: "/classrooms",
        component: () => import("@/views/Classrooms.vue"),
        name: "ClassRooms",
        meta: { requiresAuth: true },
      },
      {
        path: "/join-classroom",
        component: () => import("@/views/JoinViaQRCode.vue"),
        name: "JoinViaQRCode",
        meta: { requiresAuth: true },
      },
      {
        path: "/aboutus",
        component: () => import("@/views/AboutUs.vue"),
        name: "AboutUs",
        meta: { requiresAuth: true },
      },
      {
        path: "/introduction",
        component: () => import("@/views/Introduction.vue"),
        name: "introduction",
        meta: { requiresAuth: true },
      },
      {
        path: "/selectsora",
        component: () => import("@/components/quran/SelectSora.vue"),
        name: "selectsora",
        meta: { requiresAuth: true },
      },
      {
        path: "/settings",
        component: () => import("@/views/Settings.vue"),
        name: "Settings",
        meta: { requiresAuth: true },
      },
      {
        path: "/support-us",
        component: () => import("@/views/SupportUs.vue"),
        name: "SupportUs",
        meta: { requiresAuth: true },
      },
      {
        path: "/payment/success",
        component: () => import("@/views/PaymentSuccess.vue"),
        name: "PaymentSuccess",
        meta: { requiresAuth: false },
      },
      {
        path: "/payment/cancel",
        component: () => import("@/views/PaymentCancel.vue"),
        name: "PaymentCancel",
        meta: { requiresAuth: false },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Initialize auth on first load
let authInitialized = false;

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check auth on first navigation
  if (!authInitialized) {
    await authStore.checkAuth();
    authInitialized = true;
  }

  const isAuthenticated = authStore.authenticated;
  const hasSelectedLanguage = localStorage.getItem("locale") != null;
  const hasSelectedRiwaya = useSettingsStore().riwaya != null;

  // If route requires auth and user is not authenticated
  if (to.meta.requiresAuth && !isAuthenticated) {
    // If language not selected, go to language selection first
    if (!hasSelectedLanguage) {
      next({ name: "selectlang" });
    } else {
      // Language selected, redirect to login
      next({ name: "login" });
    }
  }
  // If user is authenticated and trying to access login/language selection, redirect to introduction
  // Allow password reset pages even if authenticated (in case user clicks email link while logged in)
  else if (
    isAuthenticated &&
    (to.name === "login" || to.name === "selectlang")
  ) {
    next({ name: "introduction" });
  }
  // Authenticated but hasn't picked a riwaya yet → force riwaya selection first
  // (mirrors the language gate). Excludes the picker itself to avoid a loop.
  else if (
    isAuthenticated &&
    to.meta.requiresAuth &&
    !hasSelectedRiwaya &&
    to.name !== "selectriwaya"
  ) {
    next({ name: "selectriwaya" });
  }
  // Check user type permissions (if needed in future)
  else if (
    to.meta.userTypeAllowed &&
    authStore.user?.userType &&
    !to.meta.userTypeAllowed.includes(authStore.user.userType)
  ) {
    next({ name: "selectlang" });
  } else {
    next();
  }
});

export default router;
